/**
 * Web-side helper for the `getPacketDownloadUrl` Cloud Function.
 *
 * The function returns a 1-hour V4 signed URL — caller must NOT
 * persist the URL anywhere durable (Firestore, audit logs). Treat
 * each invocation as a fresh, transient session URL: call → open
 * → discard.
 *
 * Wraps `httpsCallable` from `firebase/functions` so the admin-web
 * and applicant-web both consume the same shape. The Cloud Function
 * does the auth + permission + lookup; the client only needs to
 * pass the application id.
 *
 * Source-cited APIs (verified against the Firebase Web SDK 10.x):
 *   - `getFunctions(app?, region?)` -> Functions
 *   - `httpsCallable<TReq, TRes>(functions, name)` -> Callable
 */
import { type FirebaseApp } from 'firebase/app';
import {
  type Functions,
  type HttpsCallable,
  getFunctions,
  httpsCallable,
} from 'firebase/functions';

/** Wire-format input to the callable. */
export interface IGetPacketDownloadUrlRequest {
  applicationId: string;
}

/** Wire-format output from the callable. */
export interface IGetPacketDownloadUrlResponse {
  url: string;
  /** ms-epoch the signed URL stops working. */
  expiresAtMs: number;
}

/**
 * Builds a `getPacketDownloadUrl(applicationId)` function that calls
 * the Cloud Function and returns the URL. Apps wire this once at
 * Firebase init and pass the resulting closure into their UI layer.
 */
export function createPacketDownloadClient(
  app: FirebaseApp,
): (applicationId: string) => Promise<IGetPacketDownloadUrlResponse> {
  const functions: Functions = getFunctions(app);
  const callable: HttpsCallable<
    IGetPacketDownloadUrlRequest,
    IGetPacketDownloadUrlResponse
  > = httpsCallable(functions, 'getPacketDownloadUrl');

  return async (applicationId) => {
    const result = await callable({ applicationId });
    return result.data;
  };
}
